import React from "react";

// Customizable Area Start
import {
  Box,
  TextField,
  Typography,
  MenuItem,
  Button,
  IconButton,
  Select,
  Checkbox,
  InputAdornment,
  FormHelperText,
  List,
  ListItem,
  ListItemIcon,
  styled,
  Dialog,
  Link,
} from "@mui/material";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import { signupGroupLargeImg } from "./assets";
import { Field, Form, Formik } from "formik";
import * as Yup from "yup";
import clsx from "clsx";
import CheckIcon from "@mui/icons-material/Check";
import NavButton from "../../../components/src/NavButton";
import NavigationMenu from "../../navigationmenu/src/NavigationMenu.web";
import AppFooter from "../../../components/src/AppFooter.web";
import CustomInfoDialog from "../../../components/src/CustomInfoDialog.web";
import CustomInfoIcon from "../../../components/src/CustomInfoIcon.web";
import CustomButton from "../../../components/src/CustomButton.web";
import FullPageLoader from "../../../components/src/FullPageLoader.web";
import ConditionalRenderer from "../../../components/src/ConditionalRenderer.web";
import GoogleAd from "../../../components/src/GoogleAd.web";
const configJSON = require("./config.js");

export const validationSchema = Yup.object({
  firstName: Yup.string()
    .matches(/^[A-Za-z]+$/, "Only alphabets are allowed!")
    .max(30, "First name must be at most 30 characters!")
    .required("First name is required!"),
  lastName: Yup.string()
    .matches(/^[A-Za-z]+$/, "Only alphabets are allowed!")
    .max(30, "Last Name must be at most 30 characters!")
    .required("Last Name is required!"),
  phone: Yup.string()
    .transform((value) => value.replace(/[^0-9]/g, ""))
    .matches(/^\d{10}$/, "Phone number must contain 10 digits!")
    .required("Phone number is required!"),
  email: Yup.string()
    .transform((value) => value?.trim())
    .email("Invalid email format!")
    .matches(
      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      "Invalid email format!"
    )
    .required("Email is required!"),
  password: Yup.string()
    .min(8, "Password must be at least 8 characters")
    .max(20, "Password must be at most 20 characters")
    .required("Password is required!"),
  confirmPassword: Yup.string()
    .oneOf(
      [Yup.ref("password")],
      "Passwords do not match. Please make sure both passwords are identical."
    )
    .required("Confirm Password is required!"),
  role: Yup.string().required("Please select a role to proceed."),
  agreeToTerms: Yup.boolean().oneOf(
    [true],
    "You must agree to the terms and conditions."
  ),
});

const SignUpCompleteWrapper = styled(Box)({
  display: "flex",
  padding: "70px 30px",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  maxWidth: "650px",
  gap: "30px",
  height: "700px",
  "@media (max-width: 767px)": {
    width: "100%",
  },
});

const SignUpCompleteDescription = styled(Box)({
  color: "#64748B",
  fontSize: "1rem",
  fontWeight: 400,
  textAlign: "center",
});

const ContactUsButton = styled(Button)({
  textTransform: "none",
  color: "#652784",
  fontSize: "1rem",
  fontWeight: 500,
  marginTop: "-5px",
  padding: "0px",
});

const ErrorText = styled(Typography)({
  color: "#E11B23",
  fontSize: "0.9rem",
  fontWeight: "500",
});

const OTPDialogWrapper = styled(Dialog)({
  "& .MuiPaper-root": {
    borderRadius: "20px",
    padding: "30px",
    width: "100%",
    maxWidth: "500px",
  },
});

const OTPCloseButton = styled(IconButton)({
  padding: "4px",
  "&:hover": {
    backgroundColor: "rgba(0,0,0,0.05)",
  },
});

const RoleMenuItem = styled(MenuItem)({
  color: "#64748B",
});
// Customizable Area End

import EmailAccountRegistrationController, {
  Props,
  requirementsForPass,
  requirementsForConfirmPass,
  TypographyView,
  webStyle,
} from "./EmailAccountRegistrationController";

const RoundedCheckbox = styled(Checkbox)({
  "& .MuiSvgIcon-root": {
    width: 20,
    height: 20,
  },
  "& .MuiCheckbox-root": {
    borderRadius: "6px",
  },
});

export default class EmailAccountRegistration extends EmailAccountRegistrationController {
  constructor(props: Props) {
    super(props);
    // Customizable Area Start
    // Customizable Area End
  }
  // Customizable Area Start
  render() {
    const {
      isLoading,
      isAccountDetailsFilled,
      isPhoneVerified,
      verifyPhoneOtp,
      otpError,
      apiError,
    } = this.state;

    return (
      <TypographyView>
        <FullPageLoader isLoading={isLoading} />
        <NavigationMenu navigation={this.props.navigation} id={""} />
        <Box className="signupContainer">
          <Box component="form" className="signupLeftCon">
            <Box className="signupLeftConInsideCard">
              {isAccountDetailsFilled ? (
                <SignUpCompleteWrapper>
                  <CustomInfoIcon infoType="success" />
                  <Typography className="mainTitleText" textAlign="center">
                    {configJSON.signingUpThankYouText}
                  </Typography>
                  <SignUpCompleteDescription>
                    {this.getSignUpCompletedMessage()}
                  </SignUpCompleteDescription>
                  <CustomButton
                    data-test-id="resend-email-btn"
                    customVariant="filled"
                    sx={{ height: "56px", width: "100%", maxWidth: "360px" }}
                    onClick={this.emailVerificationLinkApi}
                  >
                    {configJSON.resendActivationLinkText}
                  </CustomButton>
                  <ContactUsButton
                    data-test-id="contact-us"
                    onClick={this.handleContactUsRedirect}
                  >
                    {configJSON.contactUsText}
                  </ContactUsButton>
                </SignUpCompleteWrapper>
              ) : (
                <>
                  <Typography className="mainTitleText">
                    Welcome to Bharat Stage
                  </Typography>
                  <Typography className="subTitle">
                    Enter your details to sign up!
                  </Typography>
                  <Formik
                    data-test-id="formik"
                    innerRef={this.formikRef}
                    initialValues={{
                      firstName: "",
                      lastName: "",
                      email: "",
                      phone: "",
                      password: "",
                      confirmPassword: "",
                      role: "",
                      agreeToTerms: false,
                    }}
                    validateOnChange
                    validationSchema={validationSchema}
                    onSubmit={this.handleSubmit}
                    validate={(values) => {
                      if (
                        values.confirmPassword &&
                        values.confirmPassword.length >= 8
                      ) {
                        if (values.password != values.confirmPassword) {
                          this.setState({ passwordsAreIdentical: true });
                        } else {
                          this.setState({ passwordsAreIdentical: false });
                        }
                      } else {
                        this.setState({ rePasswordMatchError: true });
                      }
                    }}
                  >
                    {({
                      values,
                      errors,
                      touched,
                      setFieldValue,
                      setFieldError,
                      handleBlur,
                      dirty,
                      handleSubmit,
                    }) => {
                      const isPasswordValid =
                        values.password &&
                        requirementsForPass.every((req) =>
                          req.regex.test(values.password)
                        );
                      const isConfirmPasswordValid =
                        values.confirmPassword &&
                        requirementsForConfirmPass.every((req) =>
                          req.regex.test(values.confirmPassword)
                        );
                      const isFormValid =
                        Object.keys(errors).length === 0 &&
                        dirty &&
                        isPasswordValid &&
                        isConfirmPasswordValid &&
                        isPhoneVerified;
                      return (
                        <Form
                          data-test-id="signupForm"
                          className="signupFormCon"
                          noValidate
                          autoComplete="false"
                        >
                          <Box
                            sx={{
                              display: "flex",
                              gap: "20px",
                              "@media (max-width: 425px)": {
                                flexDirection: "column",
                                gap: "0px",
                              },
                            }}
                          >
                            <Box style={{ maxWidth: "320px", width: "100%" }}>
                              <Box component={"label"} className="labelWrapper">
                                First Name
                              </Box>
                              <Field
                                as={TextField}
                                data-test-id="input-first-name"
                                className="input"
                                name="firstName"
                                placeholder="Enter your first name"
                                fullWidth
                                variant="outlined"
                                type="text"
                                value={values.firstName}
                                inputProps={{ maxLength: 30 }}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  const cleanedValue = e.target.value.replace(
                                    /[^A-Za-z]/g,
                                    ""
                                  );
                                  setFieldValue("firstName", cleanedValue);
                                  this.setFirstName(cleanedValue);
                                  setFieldError("firstName", "");
                                }}
                                error={touched.firstName && !!errors.firstName}
                                helperText={
                                  touched.firstName && errors.firstName ? (
                                    <span className="errorText">
                                      {errors.firstName}
                                    </span>
                                  ) : (
                                    ""
                                  )
                                }
                              />
                            </Box>
                            <Box
                              style={{ maxWidth: "320px", width: "100%" }}
                              sx={{
                                "@media (max-width: 425px)": {
                                  paddingTop: "10px",
                                },
                              }}
                            >
                              <Typography
                                component={"label"}
                                className="labelWrapper"
                              >
                                Last Name
                              </Typography>
                              <Field
                                as={TextField}
                                data-test-id="input-last-name"
                                className="input"
                                name="lastName"
                                fullWidth
                                variant="outlined"
                                placeholder="Enter your last name"
                                value={values.lastName}
                                inputProps={{ maxLength: 30 }}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  const cleanedValue = e.target.value.replace(
                                    /[^A-Za-z]/g,
                                    ""
                                  );
                                  setFieldValue("lastName", cleanedValue);
                                  this.setLastName(cleanedValue);
                                  setFieldError("lastName", "");
                                }}
                                error={touched.lastName && !!errors.lastName}
                                helperText={
                                  touched.lastName && errors.lastName ? (
                                    <span className="errorText">
                                      {errors.lastName}
                                    </span>
                                  ) : (
                                    ""
                                  )
                                }
                              />
                            </Box>
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              gap: "20px",
                              "@media (max-width: 425px)": {
                                flexDirection: "column",
                                gap: "0px",
                              },
                            }}
                          >
                            <Box style={{ maxWidth: "320px", width: "100%" }}>
                              <Typography
                                component={"label"}
                                className="labelWrapper"
                              >
                                Email Address
                              </Typography>
                              <Field
                                as={TextField}
                                data-test-id="input-email"
                                className="input"
                                fullWidth
                                name="email"
                                placeholder="Enter your email address"
                                variant="outlined"
                                type="email"
                                value={this.state.email}
                                inputProps={{ maxLength: 50 }}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  setFieldValue("email", e.target.value);
                                  this.setEmail(e.target.value);
                                  setFieldError("email", "");
                                }}
                                error={touched.email && !!errors.email}
                                helperText={
                                  touched.email && errors.email ? (
                                    <span className="errorText">
                                      {errors.email}
                                    </span>
                                  ) : (
                                    ""
                                  )
                                }
                              />
                            </Box>

                            <Box
                              style={{ maxWidth: "320px", width: "100%" }}
                              sx={{
                                "@media (max-width: 425px)": {
                                  paddingTop: "10px",
                                },
                              }}
                            >
                              <Typography
                                component={"label"}
                                className="labelWrapper"
                              >
                                Mobile Number
                              </Typography>
                              <Field
                                as={TextField}
                                data-test-id="input-phone-number"
                                className="input phoneNumber"
                                fullWidth
                                name="phone"
                                type="tel"
                                inputMode="numeric"
                                variant="outlined"
                                placeholder="Mobile Number"
                                value={this.state.phone}
                                onChange={(
                                  e: React.ChangeEvent<HTMLInputElement>
                                ) => {
                                  const cleanValue = e.target.value.replace(
                                    /[^0-9]/g,
                                    ""
                                  );
                                  setFieldValue("phone", cleanValue);
                                  this.setPhoneNumber(cleanValue);
                                  setFieldError("phone", "");
                                }}
                                inputProps={{
                                  maxLength: 10,
                                }}
                                InputProps={{
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <Typography className="countryCode">
                                        +91
                                      </Typography>
                                    </InputAdornment>
                                  ),
                                  endAdornment:
                                    this.state.phone.length === 10 &&
                                    !errors.phone ? (
                                      <InputAdornment position="end">
                                        <button
                                          type="button"
                                          data-test-id="verify-button"
                                          className={clsx("verifyText", {
                                            verifiedText: isPhoneVerified,
                                          })}
                                          disabled={isPhoneVerified}
                                          onClick={this.handleVerifyBtnClick}
                                        >
                                          {isPhoneVerified
                                            ? configJSON.verifiedText
                                            : configJSON.verifyText}
                                        </button>
                                      </InputAdornment>
                                    ) : null,
                                }}
                                error={touched.phone && !!errors.phone}
                                helperText={
                                  touched.phone && errors.phone ? (
                                    <span className="errorText">
                                      {errors.phone}
                                    </span>
                                  ) : (
                                    ""
                                  )
                                }
                              />
                            </Box>
                          </Box>

                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                gap: "20px",
                                "@media (max-width: 425px)": {
                                  flexDirection: "column",
                                  gap: "0px",
                                },
                              }}
                            >
                              <Box style={{ maxWidth: "320px", width: "100%" }}>
                                <Typography
                                  component={"label"}
                                  className="labelWrapper"
                                >
                                  Password
                                </Typography>
                                <Field
                                  as={TextField}
                                  data-test-id="input-password"
                                  className="input"
                                  name="password"
                                  fullWidth
                                  type={
                                    this.state.enablePasswordField
                                      ? "password"
                                      : "text"
                                  }
                                  variant="outlined"
                                  placeholder="Enter your password"
                                  value={values.password}
                                  onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setFieldValue("password", e.target.value);
                                    setFieldError("password", "");
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <IconButton
                                        onClick={() => this.handlePassword()}
                                      >
                                        {this.state.enablePasswordField ? (
                                          <VisibilityOffOutlinedIcon
                                            sx={{ fill: "#94A3B8" }}
                                          />
                                        ) : (
                                          <VisibilityOutlinedIcon
                                            sx={{ fill: "#94A3B8" }}
                                          />
                                        )}
                                      </IconButton>
                                    ),
                                  }}
                                  error={touched.password && !!errors.password}
                                  helperText={
                                    touched.password && errors.password ? (
                                      <span className="errorText">
                                        {errors.password}
                                      </span>
                                    ) : (
                                      ""
                                    )
                                  }
                                />
                                {values.password && (
                                  <List
                                    sx={{
                                      mt: 1,
                                      display: {
                                        xs: "none",
                                        sm: "block",
                                        md: "block",
                                      },
                                    }}
                                  >
                                    {requirementsForPass.map((req, index) => {
                                      const isValid = req.regex.test(
                                        values.password
                                      );
                                      return (
                                        <ListItem
                                          key={index}
                                          className="validationsChecks"
                                          sx={{ marginTop: "5px" }}
                                        >
                                          <ListItemIcon>
                                            <InfoOutlinedIcon
                                              sx={{
                                                fill: isValid
                                                  ? "#409046"
                                                  : "#E11B23",
                                              }}
                                            />
                                          </ListItemIcon>
                                          {req.label}
                                        </ListItem>
                                      );
                                    })}
                                  </List>
                                )}

                                {values.password && (
                                  <List
                                    sx={{
                                      mt: 1,
                                      display: {
                                        xs: "block",
                                        sm: "none",
                                        md: "none",
                                      },
                                    }}
                                  >
                                    {requirementsForPass
                                      .concat(requirementsForConfirmPass)
                                      .map((req, index) => {
                                        const fieldValue = values.password;
                                        const isValid =
                                          req.regex.test(fieldValue);
                                        return (
                                          <ListItem
                                            key={index}
                                            className="validationsChecks"
                                            sx={{ marginTop: "5px" }}
                                          >
                                            <ListItemIcon>
                                              <InfoOutlinedIcon
                                                sx={{
                                                  fill: isValid
                                                    ? "#409046"
                                                    : "#E11B23",
                                                }}
                                              />
                                            </ListItemIcon>
                                            {req.label}
                                          </ListItem>
                                        );
                                      })}
                                  </List>
                                )}
                              </Box>
                              <Box
                                style={{
                                  maxWidth: "320px",
                                  width: "100%",
                                }}
                                sx={{
                                  "@media (max-width: 425px)": {
                                    paddingTop: "10px",
                                  },
                                }}
                              >
                                <Typography
                                  component={"label"}
                                  className="labelWrapper"
                                >
                                  Confirm Password
                                </Typography>
                                <Field
                                  as={TextField}
                                  data-test-id="input-confirm-password"
                                  className="input"
                                  fullWidth
                                  name="confirmPassword"
                                  type={
                                    this.state.enableReTypePasswordField
                                      ? "password"
                                      : "text"
                                  }
                                  variant="outlined"
                                  placeholder="Confirm Password"
                                  value={values.confirmPassword}
                                  {...this.txtInputConfirmPasswordProps}
                                  onChange={(
                                    e: React.ChangeEvent<HTMLInputElement>
                                  ) => {
                                    setFieldValue(
                                      "confirmPassword",
                                      e.target.value
                                    );
                                    setFieldError("confirmPassword", "");
                                  }}
                                  InputProps={{
                                    endAdornment: (
                                      <IconButton
                                        data-test-id="confirmPassword"
                                        onClick={() =>
                                          this.handleReTypePassword()
                                        }
                                      >
                                        {this.state
                                          .enableReTypePasswordField ? (
                                          <VisibilityOffOutlinedIcon
                                            sx={{ fill: "#94A3B8" }}
                                          />
                                        ) : (
                                          <VisibilityOutlinedIcon
                                            sx={{ fill: "#94A3B8" }}
                                          />
                                        )}
                                      </IconButton>
                                    ),
                                  }}
                                  error={
                                    touched.confirmPassword &&
                                    !!errors.confirmPassword
                                  }
                                  helperText={
                                    touched.confirmPassword &&
                                    errors.confirmPassword ? (
                                      <span className="errorText">
                                        {errors.confirmPassword}
                                      </span>
                                    ) : (
                                      ""
                                    )
                                  }
                                />
                                {values.password && (
                                  <List
                                    sx={{
                                      mt: 1,
                                      display: {
                                        xs: "none",
                                        sm: "block",
                                        md: "block",
                                      }, // Hide confirm password validation list for xs, sm and show on md (desktop)
                                    }}
                                  >
                                    {requirementsForConfirmPass.map(
                                      (req, index) => {
                                        const isValid = req.regex.test(
                                          values.password
                                        );
                                        return (
                                          <ListItem
                                            key={index}
                                            className="validationsChecks"
                                            sx={{ marginTop: "5px" }}
                                          >
                                            <ListItemIcon>
                                              <InfoOutlinedIcon
                                                sx={{
                                                  fill: isValid
                                                    ? "#409046"
                                                    : "#E11B23",
                                                }}
                                              />
                                            </ListItemIcon>
                                            {req.label}
                                          </ListItem>
                                        );
                                      }
                                    )}
                                  </List>
                                )}
                              </Box>
                            </Box>
                          </Box>
                          <Box sx={{ marginTop: "-5px" }}>
                            <Typography
                              component={"label"}
                              className="labelWrapper"
                            >
                              Role
                            </Typography>
                            <Field
                              as={Select}
                              data-test-id="select-role"
                              fullWidth
                              defaultValue=""
                              displayEmpty
                              name="role"
                              onChange={(e: { target: { value: any } }) => {
                                setFieldValue("role", e.target.value);
                                setFieldError("role", "");
                              }}
                              onBlur={handleBlur}
                              error={touched.role && !!errors.role}
                              inputProps={{
                                "aria-label": "Select role",
                                IconComponent: KeyboardArrowDownRoundedIcon,
                              }}
                              sx={{
                                color: "#64748b !important",
                                borderRadius: "32px",
                                "& .MuiOutlinedInput-root": {
                                  borderRadius: "32px",
                                },
                                "& .css-11u53oe-MuiSelect-select-MuiInputBase-input-MuiOutlinedInput-input":
                                  {
                                    padding: "16px 56px 16px 24px",
                                  },
                              }}
                              MenuProps={{
                                PaperProps: {
                                  sx: {
                                    backgroundColor: "#fff",
                                    borderRadius: "16px",
                                    border: "1px solid #CBD5E1",
                                    boxShadow: "none",
                                    outline: "none",
                                    marginTop: "3px",
                                  },
                                },
                              }}
                            >
                              <MenuItem
                                value=""
                                sx={{
                                  display: "none",
                                }}
                                className="menuItem"
                              >
                                Please select a role
                              </MenuItem>
                              <RoleMenuItem value="Artist" className="menuItem">
                                Artist
                              </RoleMenuItem>
                              <RoleMenuItem
                                value="Film_Maker"
                                className="menuItem"
                              >
                                Filmmaker
                              </RoleMenuItem>
                            </Field>
                            {touched.role && errors.role && (
                              <FormHelperText className="errorText">
                                {errors.role}
                              </FormHelperText>
                            )}
                          </Box>

                          <Box>
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "10px",
                                marginTop: "5px",
                              }}
                            >
                              <Field
                                as={RoundedCheckbox}
                                data-test-id="checkbox-terms"
                                checked={values.agreeToTerms}
                                onChange={() => {
                                  setFieldValue(
                                    "agreeToTerms",
                                    !values.agreeToTerms
                                  );
                                  setFieldError("agreeToTerms", "");
                                }}
                                icon={
                                  <span
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      width: 20,
                                      height: 20,
                                      borderRadius: "8px",
                                      border: "1px solid #652784",
                                    }}
                                  />
                                }
                                checkedIcon={
                                  <span
                                    style={{
                                      display: "flex",
                                      justifyContent: "center",
                                      alignItems: "center",
                                      width: 20,
                                      height: 20,
                                      borderRadius: "8px",
                                      backgroundColor: "#652784",
                                      color: "white",
                                    }}
                                  >
                                    <CheckIcon
                                      sx={{ fontSize: 16, color: "white" }}
                                    />
                                  </span>
                                }
                              />
                              <Typography className="agreeText">
                                I have read and agree to the
                                <Link
                                  target="_blank"
                                  sx={{ textTransform: "none", marginLeft: "4px" }}
                                  href="/TermsConditions"
                                  onClick={() =>
                                    this.handleTermsAndConditionClick()
                                  }
                                >
                                  Terms and Conditions
                                </Link>
                              </Typography>
                            </Box>
                            {touched.agreeToTerms && errors.agreeToTerms && (
                              <FormHelperText className="errorText">
                                {errors.agreeToTerms}
                              </FormHelperText>
                            )}
                          </Box>

                          {apiError && (
                            <Box mt="-10px">
                              <ErrorText>{apiError}</ErrorText>
                            </Box>
                          )}

                          <Box
                            sx={{ display: "flex", justifyContent: "center" }}
                          >
                            <Button
                              data-test-id="submit-button"
                              fullWidth
                              variant="contained"
                              disabled={!isFormValid}
                              className="signupBtn"
                              onClick={() => handleSubmit()}
                            >
                              {configJSON.signUpText}
                            </Button>
                          </Box>

                          <Typography variant="body2" className="signupOrText">
                            <Box></Box>
                            <Typography component={"span"}>or</Typography>
                            <Box></Box>
                          </Typography>

                          <Box className="signupAlreadyAccCon">
                            <Typography>Already have an account?</Typography>
                            <NavButton
                              dataTestId="login-button"
                              classname={""}
                              path="EmailAccountLoginBlock"
                              onClick={this.handleNavigation}
                            >
                              Log In
                            </NavButton>
                          </Box>
                        </Form>
                      );
                    }}
                  </Formik>
                </>
              )}
            </Box>
          </Box>
          <Box className="signupRightCon">
            <Box
              component={"img"}
              src={signupGroupLargeImg}
              alt="signupLargeImg"
            />
          </Box>
        </Box>
        <GoogleAd responsive format="auto" sx={{ mt: 6 }} slot="7030833469" />
        <AppFooter navigation={this.props.navigation} />
        <OTPDialogWrapper
          data-test-id="otp-modal"
          open={this.state.triggerModal}
          onClose={this.handleCloseOtpModal}
        >
          <Box display="flex" justifyContent="flex-end">
            <OTPCloseButton
              data-test-id="otp-close-button"
              onClick={this.handleCloseOtpModal}
            >
              <CloseRoundedIcon />
            </OTPCloseButton>
          </Box>
          <Box sx={{ ...webStyle.modalContainer }}>
            <Box component={"h2"} sx={{ ...webStyle.modalTitle }}>
              OTP Verification
            </Box>
            <Typography sx={{ ...webStyle.modalDesc }}>
              Please enter verification code sent to +91 {this.state.phone}
            </Typography>
            <Box sx={{ display: "flex", gap: "10px", marginTop: "15px" }}>
              {this.state.verifyPhoneOtp.map((_, index) => (
                <TextField
                  key={index}
                  data-test-id="otp-input-field"
                  type="tel"
                  inputMode="numeric"
                  autoFocus={index === 0}
                  autoComplete="off"
                  value={this.state.verifyPhoneOtp[index]}
                  onChange={(e) => this.handleOtpChange(index, e)}
                  onKeyDown={(e) => this.handleOtpKeyDown(index, e)}
                  inputRef={this.inputRefs[index]}
                  inputProps={{
                    maxLength: 1,
                  }}
                  sx={{ ...webStyle.modalInput }}
                />
              ))}
            </Box>
            <ConditionalRenderer isVisible={!!otpError}>
              <ErrorText data-test-id="otp-error">{otpError}</ErrorText>
            </ConditionalRenderer>
            <CustomButton
              fullWidth
              loaderSize={22}
              data-test-id="otp-submit-button"
              customVariant="filled"
              sx={{ maxWidth: "290px", height: "56px", mb: "15px", mt: "25px" }}
              isLoading={this.state.isVerifyingPhone}
              disabled={verifyPhoneOtp.join("").length < 4}
              onClick={this.handleSubmitOtp}
            >
              {configJSON.submitText}
            </CustomButton>
            <Button
              data-test-id="otp-resend-button"
              disabled={this.state.isLoading}
              sx={{ ...webStyle.modalResendOtpBtn }}
              onClick={this.handleSendOtp}
            >
              {configJSON.resendOtpText}
            </Button>
          </Box>
        </OTPDialogWrapper>
        <CustomInfoDialog
          isOpen={this.state.isResendEmailDialog}
          infoType={this.state.resendEmailInfoType}
          title={this.state.resendEmailMessage}
          handleClose={this.handleResendEmailDialogClose}
        />
      </TypographyView>
    );
  }
  // Customizable Area End
}
